import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Star, ExternalLink, Heart, Share2, Link2, ArrowLeft, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { bookNow } from "@/lib/bookNow";
import { useBookNowLoader } from "@/components/BookNowFlyingLoader";
import { useAuth } from "@/context/AuthContext";
import CardImage from "@/components/CardImage";
import PhotoCarousel from "@/components/PhotoCarousel";
import { toast } from "sonner";

const LOGO_URL = "/assets/logo-landscape.png";

/**
 * Public landing page for a shared card. Rendered at `/card/:id`.
 *
 * Visits here originate from `/api/share/card/<id>` (backend meta-refresh)
 * or a direct share URL. The route is intentionally NOT wrapped in
 * `<Protected>` so guests can view + book without hitting the auth
 * wall or the referral onboarding.
 *
 * Guest behavior:
 *   • Book Now  → hits /api/booking-clicks/outbound (anonymous, still
 *                 CJ-tracked via anonymous sid).
 *   • Save     → toast "Sign up to save trips".
 *   • Share    → uses native share sheet with a clean card URL.
 *   • Log in / Sign up → /auth?next=/card/<id> (returns after login).
 *   • Continue as guest → dismisses the auth panel, stays on the card.
 *
 * Logged-in visitors get the same layout — but the Save action posts
 * to /destinations/:id/swipe (matching the CardDetailDialog contract).
 */
export default function CardSharePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errText, setErrText] = useState("");
  const [saved, setSaved] = useState(false);
  const [showAuthNudge, setShowAuthNudge] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrText("");
      try {
        const { data } = await api.get(`/cards/${encodeURIComponent(id)}/public`);
        if (!cancelled) setCard(data);
      } catch (err) {
        if (!cancelled) {
          const status = err?.response?.status;
          setErrText(
            status === 404
              ? "This card is no longer available."
              : (formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't load this card."),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const locationStr = useMemo(() => {
    if (!card) return "";
    return [card.city, card.state, card.country].filter(Boolean).join(", ");
  }, [card]);

  const currSymbol = card?.currency === "EUR" ? "\u20AC" : "$";

  const nextUrl = `/card/${encodeURIComponent(id || "")}`;
  const goAuth = (mode) => {
    const q = new URLSearchParams({ next: nextUrl });
    if (mode === "register") q.set("mode", "register");
    nav(`/auth?${q.toString()}`);
  };

  const { setLoading: setBookNowLoading } = useBookNowLoader();
  const doBook = async () => {
    if (!card) return;
    // bookNow handles both auth + guest — the outbound endpoint uses
    // Depends(current_user_optional) so an anonymous CJ redirect is
    // fine; sid is server-injected.
    await bookNow({ card, onLoadingChange: setBookNowLoading });
  };

  const doSave = async () => {
    if (!user) {
      toast.info("Sign up to save trips \u2726", {
        action: { label: "Sign up", onClick: () => goAuth("register") },
      });
      return;
    }
    if (saved) return;
    setSaved(true);
    try {
      await api.post(`/destinations/${encodeURIComponent(card.id)}/swipe`, { direction: "save" });
      // 2026-07-15 #polish2 — removed "saved" toast; the bookmark button
      // visually flips to filled state.
    } catch (err) {
      setSaved(false);
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't save right now.");
    }
  };

  const doShare = async () => {
    if (!card) return;
    // Clean share URL — points at THIS page directly. No referral
    // suffix, no OG-wrapper path leaking into the message body.
    const url = card.share_page_url || (typeof window !== "undefined" ? `${window.location.origin}/card/${encodeURIComponent(card.id)}` : "");
    const title = card.name || "TravelBio";
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text: title, url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied \u2708");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const doCopyLink = async () => {
    if (!card) return;
    const url = card.share_page_url || `${window.location.origin}/card/${encodeURIComponent(card.id)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const chips = (card?.categories || []).filter(Boolean);

  // Loading + error states —
  if (loading) {
    return (
      <div data-testid="card-share-loading" className="min-h-screen w-full bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-tb-navy/60 animate-spin" />
      </div>
    );
  }
  if (errText || !card) {
    return (
      <div data-testid="card-share-error" className="min-h-screen w-full bg-white flex flex-col items-center justify-center gap-4 p-6 text-center">
        <img src={LOGO_URL} alt="TravelBio" className="h-12 w-auto" />
        <p className="font-serif-it italic text-tb-navy/80 text-lg">{errText || "This card is no longer available."}</p>
        <Link to="/home" className="font-hand text-sm text-tb-navy underline underline-offset-4">Go to TravelBio home</Link>
      </div>
    );
  }

  return (
    <div data-testid="card-share-page" className="min-h-screen w-full bg-white">
      {/* Header — real bitmap logo, NOT the scripty BrandLogo used
          on the ReferralLandingPage. Left-align so it reads as a
          brand banner, not a marketing centerpiece. */}
      <header
        data-testid="card-share-header"
        className="w-full bg-tb-navy-deep border-b-2 border-tb-gold/40"
      >
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link
            to="/home"
            data-testid="card-share-brand-link"
            className="flex items-center gap-2"
            aria-label="TravelBio home"
          >
            <img
              src={LOGO_URL}
              alt="TravelBio"
              data-testid="card-share-brand-logo"
              className="h-8 md:h-10 w-auto"
            />
          </Link>
          {!user && (
            <button
              type="button"
              data-testid="card-share-header-login"
              onClick={() => goAuth("login")}
              className="font-hand text-sm text-tb-gold-bright underline underline-offset-2 hover:text-tb-gold"
            >
              Log in
            </button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-3 md:p-6">
        <div className="bg-white rounded-2xl border-2 border-tb-cream-3 overflow-hidden shadow-xl">
          {/* Photo carousel — fall back to CardImage placeholder */}
          <div className="p-3 pb-0">
            {card.images && card.images.length > 0 ? (
              <PhotoCarousel photos={card.images} testId="card-share-carousel" />
            ) : (
              <div className="rounded-2xl overflow-hidden border-2 border-tb-cream-3 aspect-[16/10]">
                <CardImage card={card} className="w-full h-full" size="lg" />
              </div>
            )}
          </div>

          <div className="p-4 md:p-6 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <h1
                  data-testid="card-share-name"
                  className="font-serif-it italic text-tb-navy text-2xl md:text-3xl leading-tight"
                >
                  {card.name || "TravelBio destination"}
                </h1>
                {locationStr && (
                  <p
                    data-testid="card-share-location"
                    className="mt-1 flex items-center gap-1 font-hand text-sm text-tb-navy/65"
                  >
                    <MapPin className="w-3.5 h-3.5" /> {locationStr}
                  </p>
                )}
                {card.rating != null && (
                  <p className="mt-0.5 flex items-center gap-1 font-hand text-xs text-tb-navy/70">
                    <Star className="w-3 h-3 text-tb-gold" fill="currentColor" strokeWidth={0} />
                    {Number(card.rating).toFixed(1)}
                  </p>
                )}
              </div>
              {card.price_from != null && (
                <div className="flex flex-col items-end leading-tight">
                  <span className="font-serif text-xl md:text-2xl font-semibold text-tb-navy">
                    {currSymbol}{Math.round(card.price_from).toLocaleString()}
                  </span>
                  {card.price_label && (
                    <span className="font-hand text-xs text-tb-navy/55">{card.price_label}</span>
                  )}
                </div>
              )}
            </div>

            {/* Raw Google `primaryType` chips (e.g. resort_hotel,
                lodging) are suppressed from the UI 2026-07 — kept on
                the data model for backend filter logic only. */}
            {false && chips.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {chips.map((c) => (
                  <span
                    key={c}
                    className="px-2 py-0.5 rounded-full bg-tb-navy/5 border border-tb-navy/10 font-hand text-[11px] text-tb-navy/75"
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}

            <div
              data-testid="card-share-description"
              className={
                "font-serif text-tb-navy/80 leading-relaxed text-sm md:text-base " +
                ((card.description || "").trim() ? "" : "italic text-tb-navy/50")
              }
            >
              {(card.description || "").trim()
                ? card.description
                : "Description coming soon \u2014 help us add one by contributing your review after your trip."}
            </div>

            <div className="pt-2 flex flex-wrap items-center gap-2 border-t border-tb-cream-3">
              <button
                type="button"
                data-testid="card-share-book-btn"
                onClick={doBook}
                className="inline-flex items-center gap-1.5 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold px-4 h-10 font-hand text-sm tb-tap hover:brightness-110"
              >
                <ExternalLink className="w-4 h-4" />
                Book Now
              </button>
              <button
                type="button"
                data-testid="card-share-save-btn"
                onClick={doSave}
                className={
                  "inline-flex items-center gap-1.5 rounded-full px-4 h-10 font-hand text-sm tb-tap " +
                  (saved
                    ? "bg-tb-coral text-white"
                    : "bg-tb-cream-2 text-tb-navy border-2 border-tb-cream-3 hover:border-tb-coral/60")
                }
              >
                <Heart className="w-4 h-4" fill={saved ? "currentColor" : "none"} />
                {saved ? "Saved" : "Save"}
              </button>
              <button
                type="button"
                data-testid="card-share-native-share-btn"
                onClick={doShare}
                className="inline-flex items-center gap-1.5 rounded-full bg-tb-cream-2 text-tb-navy border-2 border-tb-cream-3 px-4 h-10 font-hand text-sm tb-tap hover:border-tb-navy/40"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
              <button
                type="button"
                data-testid="card-share-copy-link-btn"
                onClick={doCopyLink}
                className="inline-flex items-center gap-1.5 rounded-full bg-tb-cream-2 text-tb-navy border-2 border-tb-cream-3 px-4 h-10 font-hand text-sm tb-tap hover:border-tb-navy/40"
              >
                <Link2 className="w-4 h-4" />
                Copy link
              </button>
            </div>
          </div>
        </div>

        {/* Guest auth panel — visible only when logged out AND the
            user hasn't clicked "Continue as guest". Purposely below the
            card so viewing/booking is never blocked. No points, no
            invites, no referral flow. */}
        {!user && showAuthNudge && (
          <div
            data-testid="card-share-auth-panel"
            className="mt-4 md:mt-6 bg-tb-navy-deep text-tb-cream rounded-2xl p-5 md:p-6 border-2 border-tb-gold/40 flex flex-col gap-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-serif-it italic text-lg md:text-xl">
                  Save this trip to your journal
                </p>
                <p className="mt-1 font-hand text-sm text-tb-cream/75">
                  Log in or sign up to save cards, comment, and track your booked stays.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="card-share-signup-btn"
                onClick={() => goAuth("register")}
                className="inline-flex items-center gap-1.5 rounded-full bg-tb-gold text-tb-navy-deep px-4 h-10 font-hand text-sm tb-tap hover:brightness-110"
              >
                Log in / Sign up
              </button>
              <button
                type="button"
                data-testid="card-share-guest-btn"
                onClick={() => setShowAuthNudge(false)}
                className="inline-flex items-center gap-1.5 rounded-full bg-transparent text-tb-cream border-2 border-tb-cream/40 px-4 h-10 font-hand text-sm tb-tap hover:border-tb-cream/70"
              >
                Continue as guest
              </button>
            </div>
          </div>
        )}

        {/* Footer link out — small, unobtrusive. */}
        <div className="mt-6 text-center">
          <Link
            to="/home"
            data-testid="card-share-home-link"
            className="inline-flex items-center gap-1 font-hand text-xs text-tb-navy/55 hover:text-tb-navy/80"
          >
            <ArrowLeft className="w-3 h-3" />
            Discover more on TravelBio
          </Link>
        </div>
      </main>
    </div>
  );
}
